/**
 * SETTING A CREDENTIAL FROM A MAILED TOKEN — the invitation, and the password reset.
 *
 * One screen for two routes, because they are the same act performed by two people at two moments:
 * somebody holding a token that reached their mailbox types a password, and an account ends up with
 * a first factor. `auth.invitation.accept` creates the account; `auth.password.reset_complete`
 * replaces the credential on one that exists. Everything else about the two is identical, including
 * the parts that matter most.
 *
 * ── NEITHER ONE ISSUES A SESSION, AND THAT IS THE WHOLE POINT ──────────────────────────────────
 *
 * `auth.password.reset_complete` says so in its own declaration: *"a completed reset re-establishes
 * the FIRST factor only, and an enrolled account must still present its second. Otherwise 'forgot
 * password' is a documented self-service route around MFA, and the mailbox becomes the privileged
 * boundary the Owner said it must not be."* `auth.invitation.accept` issues none either, so the
 * credential an invitee just chose is exercised once before it is trusted.
 *
 * So this screen NEVER lands anybody in the product. Both outcomes end at sign-in, and the screen
 * says that before the click rather than leaving somebody to wonder why they are not signed in.
 *
 * ── WHAT IT MAY SAY ABOUT A TOKEN: ALMOST NOTHING ──────────────────────────────────────────────
 *
 * Expired, revoked, already used and never existed are ONE answer, deliberately — a caller must not
 * be able to use this endpoint to discover which tokens exist. The single exception is
 * `password_too_short`, and the reason it is safe is structural: `evaluateRedemption` checks the
 * password length BEFORE it looks at the invitation, so that answer is identical for a live token,
 * an expired one and one that never existed. Collapsing it into the uniform failure would leave an
 * invitee who typed eleven characters holding a LIVE invitation, told only that it is "not valid",
 * with a support call as the only way forward.
 *
 * G-15 and G-10 in the ledger are the two gaps this closes: an invitation minted and delivered with
 * no browser path to redeem it, and *"a lost authenticator with spent recovery codes is a permanent
 * lockout"*.
 */

import { useState } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';

/** The locale reader, passed in so copy resolves at render and not at module load. */
type Translate = (key: TextKey) => string;
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { PasswordField } from '../components/PasswordField';
import { StateBlock } from '../components/StateBlock';
import { callOperation } from '../api/operations';
import { routes } from '../routes';

/** Which of the two acts this is. The screen states it; nothing infers it from the token. */
export type CredentialSetupKind = 'invitation' | 'password_reset';

export interface CredentialSetupScreenProps {
  readonly kind: CredentialSetupKind;
  /**
   * From the LAST PATH SEGMENT of the mailed link — `/invitation/<token>`, never `?token=`. **X8.**
   *
   * `null` when somebody reached the address by hand with no token, which this screen already has a
   * written state for. The carrier is the route's (`routePatterns.invitationAccept`); this screen only
   * ever receives the value.
   */
  readonly token: string | null;
}

interface Copy {
  readonly title: TextKey;
  readonly lead: TextKey;
  readonly submit: TextKey;
  readonly doneTitle: TextKey;
  readonly doneBody: TextKey;
}

/** The Hebrew for each act, side by side, so the two cannot quietly diverge in tone. */
export const CREDENTIAL_SETUP_HE: Readonly<Record<CredentialSetupKind, Copy>> = {
  invitation: {
    title: 'cred.acceptTitle',
    lead: 'cred.acceptLead',
    submit: 'cred.createAccount',
    doneTitle: 'cred.createdTitle',
    // Said before anybody wonders why they are not inside. No session is issued here, by design.
    doneBody: 'cred.createdBody',
  },
  password_reset: {
    title: 'cred.resetTitle',
    lead: 'cred.resetLead',
    submit: 'cred.setPassword',
    doneTitle: 'cred.updatedTitle',
    doneBody: 'cred.updatedBody',
  },
};

/** Every refusal that is not `password_too_short`. One sentence, for four different reasons. */
const notValidHe = (t: Translate): string => t('cred.linkInvalidBody');

/*
 * IT SAYS WHAT WAS REFUSED, AND NOT A WORD ABOUT THE LINK. **Owner instruction, 2026-10-23.**
 *
 * This read "הקישור עדיין תקף" — the link is still valid. The server had not looked at the link
 * when it said that: `evaluateRedemption` checks the password length FIRST, before the invitation
 * is fetched at all, so this answer is identical for a live invitation and for one that is expired,
 * revoked or never existed. The sentence turned an ordering decision into a claim the product had
 * no basis for, and on 2026-10-23 it sent exactly that reading back to us.
 *
 * The distinction itself stays — an invitee who typed eleven characters must not be told "not
 * valid" and left with a support call as the way forward — but it now promises only what it knows:
 * try again here, with a longer one.
 *
 * NO MINIMUM IS QUOTED. `MIN_PASSWORD_LENGTH` lives in the API's domain module and nothing exports
 * it to this app; writing the number here would make a second source of truth for it, which is the
 * kind of pin that goes stale while reading as fact.
 */
/**
 * THE REQUEST DID NOT GET AN ANSWER ABOUT THE LINK, and the screen says only that.
 *
 * No status, no route, no exception type — 09 §9.1 V3 allows the short reference code and nothing
 * else. It deliberately does NOT suggest asking for a new link: the invitation may be perfectly
 * good, and sending somebody to burn it would be acting on a guess this screen cannot make.
 */
const faultHe = (t: Translate): string =>
  t('cred.serverFailedBody') + t('cred.serverFailedNote');

const tooShortHe = (t: Translate): string =>
  t('cred.tooShortBody') + t('cred.tooShortNote');

/*
 * ── A SERVER FAULT IS NOT A STATEMENT ABOUT THE LINK. **The audit of 2026-10-24.** ─────────────
 *
 * The Owner's own trail settles what happened. Ten requests passed the gate on
 * `auth.invitation.accept`; SEVEN wrote `auth.invitation.redeemed` with `password_too_short`, and
 * THREE wrote no redemption row at all — the attempts with a long enough password. Every DECIDED
 * path in `services/invitation.ts` records before it returns, so a request that reached the
 * handler and recorded nothing did not decide: it THREW, after the length check and inside the
 * redemption transaction.
 *
 * This screen then printed *"הקישור אינו תקף"*, because `isTooShort` was the only refusal it
 * recognised and everything else fell through to that one sentence — a 500, a 503 and a dropped
 * connection included. **It is the same defect as the "הקישור עדיין תקף" line removed on
 * 2026-10-23, pointing the other way:** the product asserting something about a link the server
 * never judged.
 *
 * So `invitation_not_valid` — the ONE answer the route gives about the link — is now the only
 * thing that produces that sentence, and anything else is a fault with its reference.
 */
type Outcome =
  | { readonly kind: 'done' }
  | { readonly kind: 'too_short' }
  | { readonly kind: 'not_valid' }
  | { readonly kind: 'fault'; readonly requestUid: string };

export function CredentialSetupScreen({ kind, token }: CredentialSetupScreenProps): JSX.Element {
  const { t } = useLocale();
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const copy = CREDENTIAL_SETUP_HE[kind];

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (token === null || sending) return;
    setSending(true);
    setOutcome(null);

    // TWO LITERAL OPERATION IDS, on two branches. A single `callOperation(idFor(kind), …)` would be
    // the computed id G-03 refuses — one call site standing for two routes, invisible to the scan.
    const call =
      kind === 'invitation'
        ? callOperation('auth.invitation.accept', { body: { token, password } })
        : callOperation('auth.password.reset_complete', { body: { token, password } });

    void call
      .then(
        () => setOutcome({ kind: 'done' }),
        (err: unknown) => {
          setOutcome(classify(err));
        },
      )
      .finally(() => setSending(false));
  }

  return (
    <AppShell screenId="credential-setup"
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t(copy.title)}
    >
      <p className="screen-lead">{t(copy.lead)}</p>

      {token === null ? (
        <StateBlock
          titleHe={t('cred.noLinkTitle')}
          // Nothing was checked, and saying "the link is invalid" would be a claim about a link
          // nobody supplied.
          bodyHe={t('cred.noLinkBody')}
          tone="unavailable"
          nextStep={
            <a className="btn" href={routes.signIn}>
              {t('personal.goToSignIn')}
            </a>
          }
        />
      ) : outcome?.kind === 'done' ? (
        <StateBlock
          titleHe={t(copy.doneTitle)}
          bodyHe={t(copy.doneBody)}
          nextStep={
            <a className="btn btn--nav" href={routes.signIn}>
              {t('personal.goToSignIn')}
            </a>
          }
        />
      ) : (
        <form className="form" onSubmit={submit}>
          {/*
            §4, 2026-10-25: the eye, through the shared component — and the hint travels with it, so
            it is bound to the input by `aria-describedby` rather than merely sitting under it.

            THIS SCREEN IS TWO OF THE THREE PLACES. It serves `kind="invitation"` and
            `kind="password_reset"`, which are two of the three password fields the product has; the
            third is `SignInScreen`. There is no fourth, because there is no change-password route.

            NO LENGTH IS STATED IN THE HINT. The server owns the rule, and a number typed into a
            screen is a second copy of it that goes stale the day the rule changes — and reads as a
            promise the server never made. The refusal names the problem when it happens.
          */}
          <PasswordField
            labelHe={t('cred.newPassword')}
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
            hintHe={t('cred.passwordHint')}
          />

          <div className="actions-row">
            <button type="submit" className="btn btn--primary" disabled={sending}>
              {sending ? t('common.sending') : t(copy.submit)}
            </button>
          </div>
        </form>
      )}

      {outcome?.kind === 'too_short' ? (
        <StateBlock titleHe={t('cred.tooShortTitle')} bodyHe={tooShortHe(t)} tone="error" />
      ) : null}

      {outcome?.kind === 'not_valid' ? (
        <StateBlock
          titleHe={t('cred.linkInvalidTitle')}
          bodyHe={notValidHe(t)}
          tone="error"
          nextStep={
            <a className="btn" href={routes.signIn}>
              {t('personal.goToSignIn')}
            </a>
          }
        />
      ) : null}

      {outcome?.kind === 'fault' ? (
        <StateBlock
          titleHe={t('cred.serverFailedTitle')}
          bodyHe={faultHe(t)}
          tone="error"
          nextStep={
            /*
             * ── WHAT TO DO, AND THEN THE CODE. **Owner instruction, 2026-10-25.** ──────────────
             *
             * These were two ALTERNATIVES: with no reference the screen said what to do, and with
             * one it printed the code and said nothing about what to do. So whichever branch you
             * landed in, you got half the answer — and the half you got depended on whether the
             * server happened to return a reference, which is nothing to do with what the person
             * needs to know.
             *
             * `faultHe(t)` above already says what happened and what it does NOT imply. This adds the
             * action, always, and puts the code after it as secondary.
             */
            <>
              <p className="state-block__todo">
                {t('cred.retrySameScreen')}
              </p>
              {outcome.requestUid === '' ? null : (
                <p className="state-block__reference">
                  <span className="state-block__reference-label">{t('common.supportRef')}</span>{' '}
                  <code dir="ltr">{outcome.requestUid}</code>
                </p>
              )}
            </>
          }
        />
      ) : null}
    </AppShell>
  );
}

/**
 * WHAT THE SERVER ACTUALLY SAID, read from its declared codes and from nothing else.
 *
 * `routes/public.ts` gives this route exactly three answers: `201`, `400 password_too_short`, and
 * `400 invitation_not_valid` — the one shape that stands for expired, revoked, already used and
 * unknown together. Anything else is the route NOT answering: a fault, a timeout, a dropped
 * connection.
 *
 * Read narrowly on purpose, in both directions. An unrecognised 400 is NOT treated as a statement
 * about the link, so a refusal nobody has taught this screen about cannot start disclosing
 * something by being rendered with its own words; and it is not reported as a fault either —
 * `invitation_not_valid` is the uniform answer and a 400 that is not one of the two named codes is
 * still a refusal, so it keeps the uniform sentence.
 */
function classify(err: unknown): Outcome {
  const status = (err as { status?: unknown } | null)?.status;
  const requestUid = (err as { requestUid?: unknown } | null)?.requestUid;
  const code = ((err as { body?: unknown } | null)?.body as { error?: unknown } | null)?.error;

  if (code === 'password_too_short') return { kind: 'too_short' };
  if (status === 400) return { kind: 'not_valid' };
  return { kind: 'fault', requestUid: typeof requestUid === 'string' ? requestUid : '' };
}
